import React, { useCallback, useState } from 'react';
import { Layout, message } from 'antd';
import styled from '@emotion/styled';
import useSWR from 'swr';
import {
  AccessCodeFilter,
  AccessCodeModal,
  AccessCodeModalExport,
  AccessCodePageHeader,
  AccessCodePageTable,
} from '../../organisms/access-code';
import { PAGE_SIZE_OPTIONS, PER_PAGE_DEFAULT } from '../../../constants/common';
import { API_URLS } from '../../../constants/urls/api-urls';
import {
  getAccessCodeRepository,
  getPurposeRepository,
} from '../../../repository/mytm/access-code/accessCodeRepository';
import {
  AccessCodeRecordInterface,
  AccessCodeStatus,
  SubscriptionDurationValue,
  SubscriptionPlanValue,
} from '../../../types/accessCode';
import useDebounce from '../../../hooks/debounced';
import { MESSAGE_ERROR } from '../../../constants/message';
import { ActiveSort } from '../../molecules/title/TitleTable';
import { appStyles } from '../../../styles';

const { Content } = Layout;

export interface OptionSearchAccessCode {
  strSearch?: string;
  plan?: number;
  duration?: number;
  used?: number;
  filterEmail?: ActiveSort;
  status?: string;
}

const DEFAULT_SEARCH = {
  strSearch: '',
  plan: SubscriptionPlanValue.ALL,
  duration: SubscriptionDurationValue.ALL,
  status: AccessCodeStatus.ALL,
};

export const AccessCode = () => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PER_PAGE_DEFAULT);
  const [optionSearch, setOptionSearch] = useState<OptionSearchAccessCode>(DEFAULT_SEARCH);
  const [loading] = useState(false);
  const [isOpenModalGenerate, setIsOpenModalGenerate] = useState(false);
  const [isOpenModalExport, setIsOpenModalExport] = useState(false);
  const debouncedSearch = useDebounce(optionSearch.strSearch, 500);
  const [purposeList, setPurposeList] = useState<any[]>();

  const { data, isValidating, mutate, error } = useSWR(
    [
      API_URLS.ACCESS_CODE_LIST,
      page,
      pageSize,
      optionSearch.status,
      optionSearch.plan,
      optionSearch.duration,
      optionSearch.filterEmail,
      debouncedSearch,
    ],
    (
      url: string,
      pageNumber: number,
      pageSizeNumber: number,
      status: string,
      plan: any,
      duration: any,
      filterEmail: any,
      strSearch: any,
    ) =>
      getAccessCodeRepository(url, {
        page: pageNumber,
        take: pageSizeNumber || PER_PAGE_DEFAULT,
        strSearch: strSearch.trim(),
        plan: plan || undefined,
        duration: duration !== SubscriptionDurationValue.ALL ? duration : undefined,
        filterEmail,
        status:
          status === AccessCodeStatus.ALL
            ? `${AccessCodeStatus.AVAILABLE}, ${AccessCodeStatus.ACTIVE},${AccessCodeStatus.INACTIVE},${AccessCodeStatus.EXPIRED}`
            : status,
      }),
    {
      revalidateOnFocus: false,
      refreshInterval: 600000,
    },
  );

  const handleGetPurpose = useCallback(async () => {
    try {
      const dataPurposeList = await getPurposeRepository();
      if (dataPurposeList && dataPurposeList?.data.length > 0) {
        setPurposeList(dataPurposeList?.data.map((r: any, index: number) => ({ ...r, index })));
      } else {
        setPurposeList([]);
      }
    } catch (e) {
      message.error(MESSAGE_ERROR.M02);
      setPurposeList([]);
    }
  }, []);

  const getFilteredData = useCallback((filterValue: OptionSearchAccessCode) => {
    setOptionSearch(old => ({ ...old, ...filterValue }));
    setPage(1);
    setPageSize(PER_PAGE_DEFAULT);
  }, []);

  const handleChangePage = useCallback((pg: number, pgSize?: number) => {
    setPage(pg);
    setPageSize(pgSize || PER_PAGE_DEFAULT);
  }, []);

  const onShowSizeChange = useCallback((current: number, size: number) => {
    setPage(current);
    setPageSize(size || PER_PAGE_DEFAULT);
  }, []);

  const onCloseModalGenerate = useCallback(() => {
    setIsOpenModalGenerate(false);
  }, []);

  const openModalGenerate = useCallback(() => {
    setIsOpenModalGenerate(true);
  }, []);

  const onCloseModalExport = useCallback(() => {
    setIsOpenModalExport(false);
  }, []);

  const openModalExport = useCallback(() => {
    setIsOpenModalExport(true);
    handleGetPurpose();
  }, [handleGetPurpose]);

  if (error) {
    message.error(MESSAGE_ERROR.M02);
  }

  const recordAccessCode = data
    ? data.data.map((item: AccessCodeRecordInterface, index: number) => ({ ...item, ...{ index } }))
    : [];

  return (
    <>
      <Container>
        <Content>
          <AccessCodePageHeader openModalGenerate={openModalGenerate} openModalExport={openModalExport} />
          <AccessCodeFilter options={optionSearch} getFilteredData={getFilteredData} />
          <AccessCodePageTable
            records={recordAccessCode}
            loading={loading || isValidating}
            pagination={{
              showSizeChanger: true,
              onChange: handleChangePage,
              pageSize,
              current: page,
              total: data ? data.total : 0,
              onShowSizeChange,
              pageSizeOptions: PAGE_SIZE_OPTIONS,
            }}
            onSetFilterEmail={getFilteredData}
            onRefresh={mutate}
            className="access-code-table"
          />
        </Content>
        <AccessCodeModal isOpenModal={isOpenModalGenerate} onClose={onCloseModalGenerate} onRefresh={mutate} />
        <AccessCodeModalExport isOpenModal={isOpenModalExport} onClose={onCloseModalExport} purposeList={purposeList} />
      </Container>
    </>
  );
};

const Container = styled.div`
  ${appStyles.paddingComponent}

  & .ant-pagination {
    float: right;
    padding: 15px 0 0 0;
  }

  .btn-generate-access-code,
  .btn-export-access-code {
    width: 140px;
  }
`;
